import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Logo from "../../assets/img/mainLogo.webp";
import { useTranslation } from 'react-i18next';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { currentUser, logout } = useAuth();
    const navigate = useNavigate();
    const dropdownRef = useRef(null);
    const languageRef = useRef(null);

    const location = useLocation();
    const { t, i18n } = useTranslation();

    const toggleNavbar = () => setIsOpen(!isOpen);
    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

    const handleLogout = () => {
        logout();
        setIsDropdownOpen(false);
        navigate('/');
    };

    // Language dropdown
    const [langOpen, setLangOpen] = useState(false);
    const languages = [
        { code: 'en', label: 'English', flag: '🇺🇸' },
        { code: 'ar', label: 'العربية', flag: '🇸🇦' },
    ];

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
        localStorage.setItem('appLanguage', lang);
        setLangOpen(false);
    };

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (languageRef.current && !languageRef.current.contains(event.target)) {
                setLangOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Scroll handlers
    const handleContactClick = (e) => {
        e.preventDefault();
        const section = document.getElementById("get-in-touch-section");
        if (section) section.scrollIntoView({ behavior: "smooth" });
        else {
            navigate('/');
            setTimeout(() => document.getElementById("get-in-touch-section")?.scrollIntoView({ behavior: "smooth" }), 100);
        }
    };

    const handleBrandsClick = (e) => {
        e.preventDefault();
        const section = document.getElementById("brands-section");
        if (section) section.scrollIntoView({ behavior: "smooth" });
        else {
            navigate('/');
            setTimeout(() => document.getElementById("brands-section")?.scrollIntoView({ behavior: "smooth" }), 100);
        }
    };

    const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

    return (
        <nav className="navbar navbar-expand-lg w-100 position-fixed top-0 p-0">
            <div className="container">
                <Link className="navbar-brand p-0 pt-1" to="/">
                    <img src={Logo} alt="Akad Gate Pharmacy" loading='lazy' />
                </Link>
                <button
                    className="navbar-toggler"
                    type="button"
                    onClick={toggleNavbar}
                    aria-label="Toggle navigation"
                >
                    <span className="navbar-toggler-icon"></span>
                </button>

                <div className={`collapse navbar-collapse ${isOpen ? 'show' : ''}`} id="navbarSupportedContent">
                    <ul className="navbar-nav align-items-center ms-auto mb-2 mb-lg-0">

                        <li className="nav-item">
                            <Link className={`nav-link ${location.pathname === "/" ? "active" : ""}`} to="/">
                                {t('home')}
                            </Link>
                        </li>
                        <li className="nav-item">
                            <Link className={`nav-link ${location.pathname === "/shop" ? "active" : ""}`} to="/shop">
                                {t('shop')}
                            </Link>
                        </li>
                        <li className="nav-item">
                            <a className="nav-link" href="#brands-section" onClick={handleBrandsClick}>
                                {t('brands')}
                            </a>
                        </li>
                        <li className="nav-item">
                            <Link className={`nav-link ${location.pathname === "/aboutus" ? "active" : ""}`} to="/aboutus">
                                {t('about us')}
                            </Link>
                        </li>
                        <li className="nav-item">
                            <a className="nav-link" href="#get-in-touch-section" onClick={handleContactClick}>
                                {t('contact us')}
                            </a>
                        </li>

                        {/* User Account */}
                        <li className="account-btn" ref={dropdownRef}>
                            {currentUser ? (
                                <div className="dropdown">
                                    <button className="btn p-0 border-0" onClick={toggleDropdown} aria-label='User Menu'>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-user" width="44" height="44" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6f32be" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                            <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0"/>
                                            <path d="M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2"/>
                                        </svg>
                                    </button>
                                    <ul className={`dropdown-menu dropdown-menu-end ${isDropdownOpen ? 'show' : ''}`} style={{ display: isDropdownOpen ? 'block' : 'none' }}>
                                        <li><span className="dropdown-item-text">Welcome, {currentUser.name}</span></li>
                                        <li><hr className="dropdown-divider" /></li>
                                        <li><Link className="dropdown-item" to="/profile" onClick={() => setIsDropdownOpen(false)}>Profile</Link></li>
                                        <li><Link className="dropdown-item" to="/orders" onClick={() => setIsDropdownOpen(false)}>My Orders</Link></li>
                                        <li><button className="dropdown-item text-danger" onClick={handleLogout}>Logout</button></li>
                                    </ul>
                                </div>
                            ) : (
                                <Link to="/login" aria-label='Login'>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-login" width="44" height="44" viewBox="0 0 24 24" strokeWidth="1.5" stroke="#6f32be" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M15 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"/>
                                        <path d="M21 12h-13l3 -3"/>
                                        <path d="M11 15l-3 -3"/>
                                    </svg>
                                </Link>
                            )}
                        </li>

                        {/* Language Selector */}
                        <li className="languages-selector" ref={languageRef} style={{ position: 'relative', display: 'inline-block', marginLeft: '15px' }}>
                            <button className="language-dropdown-btn" onClick={() => setLangOpen(!langOpen)}>
                                <span className="flag">{currentLang.flag}</span>
                                <span className="label">{currentLang.label}</span>
                                <i className={`fa-solid fa-caret-${langOpen ? 'up' : 'down'}`} style={{ marginLeft: '5px' }}></i>
                            </button>

                            {langOpen && (
                                <div className="language-dropdown-menu" style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    background: '#fff',
                                    border: '1px solid #ccc',
                                    borderRadius: '6px',
                                    minWidth: '140px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                    zIndex: 100,
                                    overflow: 'hidden'
                                }}>
                                    {languages.map(lang => (
                                        <button
                                            key={lang.code}
                                            className={`language-item ${i18n.language === lang.code ? 'active' : ''}`}
                                            onClick={() => changeLanguage(lang.code)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: 'none',
                                                background: 'none',
                                                cursor: 'pointer',
                                                textAlign: 'left',
                                                fontSize: '14px'
                                            }}
                                        >
                                            <span className="flag">{lang.flag}</span>
                                            <span className="label">{lang.label}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </li>

                    </ul>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
